/**
 * LivePreview — a read-only product card that mirrors the editor form in real
 * time (EPIC-006 Phase 7, Task 7.2).
 *
 * It is `aria-hidden` (decorative — the editable form is the accessible source
 * of truth), updates synchronously with the form state (well under the 100 ms
 * budget; it is a pure render of props), and formats `price_kgs` RU-style at
 * the display boundary (no tiyin). Tokens are `--g-*` only.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
  type StockState,
} from "@/features/seller-dashboard/api";
import { fmtNum } from "@/lib/format";
import { parsePriceInput } from "@/features/seller-dashboard/schemas";

function stockToneClass(state: string): string {
  switch (state) {
    case "in_stock":
      return "bg-[var(--g-success)]/10 text-[var(--g-success)]";
    case "low":
      return "bg-[var(--g-warn)]/10 text-[var(--g-warn)]";
    case "out_of_stock":
      return "bg-[var(--g-error)]/10 text-[var(--g-error)]";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export interface LivePreviewProps {
  name: string;
  price: string;
  stockState: StockState;
  thumbUrl: string | null;
}

export function LivePreview({
  name,
  price,
  stockState,
  thumbUrl,
}: LivePreviewProps): ReactElement {
  registerSellerDashboardI18n();
  const { t } = useTranslation(SELLER_DASHBOARD_NS);

  const parsed = parsePriceInput(price);
  let priceText = t("preview.noPrice");
  if (parsed !== null && !Number.isNaN(parsed) && parsed > 0) {
    const fixed = parsed.toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    priceText = `${fmtNum(Number(intPart))},${decPart} ${t("editor.pricing.unit")}`;
  }

  return (
    <aside
      aria-hidden="true"
      data-testid="live-preview"
      className="rounded-[var(--g-radius)] border border-border bg-card p-4"
    >
      <p className="mb-3 text-xs font-semibold uppercase text-muted-foreground">
        {t("preview.title")}
      </p>
      <div className="overflow-hidden rounded-[var(--g-radius)] border border-border">
        <div className="flex aspect-square items-center justify-center bg-muted">
          {thumbUrl ? (
            <img src={thumbUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="text-xs text-muted-foreground">
              {t("list.noPhoto")}
            </span>
          )}
        </div>
        <div className="p-3">
          <p
            className="truncate text-sm font-semibold text-foreground"
            data-testid="preview-name"
          >
            {name.trim() === "" ? t("preview.noName") : name}
          </p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span
              className="tabular-nums text-sm text-foreground"
              data-testid="preview-price"
            >
              {priceText}
            </span>
            <span
              className={[
                "inline-flex h-6 items-center rounded-full px-2.5 text-xs font-medium",
                stockToneClass(stockState),
              ].join(" ")}
            >
              {t(`stock.${stockState}`)}
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default LivePreview;
